import { useState, useCallback, useMemo } from "react";
import log from "logbeacon";
import { useAddAssets } from "./useAddAssets";
import { Asset, SelectedAssetIds } from "./types";
import { favoritesCenter } from "@/src/store/favorite";
import {
  PositionAssetTypeEnum,
  AddPositionAsset,
} from "@/src/features/api/memberApi";

/**
 * useAddAssetsWithBatchAdd Hook 的参数
 */
interface UseAddAssetsWithBatchAddParams {
  /** 推荐的股票列表 */
  recommendedStocks: Asset[];
  /** 推荐的加密货币列表 */
  recommendedCrypto: Asset[];
  /** 选择变化时的回调 */
  onSelectionChange?: () => void;
  onError?: (error?: string | undefined) => void;
  onSuccess?: () => void;
}

/**
 * useAddAssetsWithBatchAdd Hook 的返回值
 */
interface UseAddAssetsWithBatchAddReturn {
  /** 按分类分组的选中资产 ID */
  selectedByCategory: SelectedAssetIds;
  /** 选中的资产总数 */
  selectedCount: number;
  /** 处理选择变化 */
  handleSelectionChange: (
    category: "stocks" | "crypto",
    changedId: string,
    newSelectedIds: string[],
  ) => void;
  /** 是否正在添加中 */
  isAdding: boolean;
  /** 处理批量添加资产 */
  handleAddClick: () => Promise<void>;
}

/**
 * 公共 Hook：管理资产选择状态和批量添加逻辑
 *
 * 这个 hook 封装了以下功能：
 * 1. 从推荐列表中初始化选中状态
 * 2. 管理选中资产的状态
 * 3. 批量添加选中的资产到收藏夹
 *
 * @param params - Hook 参数
 * @returns Hook 返回值
 */
export const useAddAssetsWithBatchAdd = ({
  recommendedStocks,
  recommendedCrypto,
  onSelectionChange,
  onSuccess,
  onError,
}: UseAddAssetsWithBatchAddParams): UseAddAssetsWithBatchAddReturn => {
  /**
   * 合并所有资产，用于全选/反选功能
   */
  const initialSelectedIds = useMemo(
    () => ({
      stocks: recommendedStocks
        .filter((asset) => asset.isSelected)
        .map((asset) => asset.id),
      crypto: recommendedCrypto
        .filter((asset) => asset.isSelected)
        .map((asset) => asset.id),
    }),
    [recommendedStocks, recommendedCrypto],
  );

  const { selectedByCategory, selectedCount, handleSelectionChange } =
    useAddAssets({
      initialSelectedIds,
      onSelectionChange: onSelectionChange || (() => {}),
    });

  /**
   * 处理中状态：跟踪批量添加操作是否正在进行
   */
  const [isAdding, setIsAdding] = useState(false);

  /**
   * 处理添加按钮点击
   * 批量添加选中的资产到收藏夹，使用 batchAddAssets 方法
   */
  const handleAddClick = useCallback(async () => {
    if (isAdding) {
      return; // 防止重复点击
    }
    setIsAdding(true);
    try {
      // 1. 获取用户的收藏夹ID
      const foldersResult = await favoritesCenter.fetchUserFolders({
        limit: 1,
        forceRefresh: false,
      });

      if (!foldersResult.success || !foldersResult.data?.length) {
        log.error(
          "[useAddAssetsWithBatchAdd] 获取收藏夹失败",
          foldersResult.error || "未找到收藏夹",
        );
        setIsAdding(false);
        onError?.(foldersResult.error || "get folder failed");
        return;
      }
      const folderId = foldersResult.data[0].id;
      if (!folderId) {
        log.error("[useAddAssetsWithBatchAdd] 收藏夹ID为空");
        setIsAdding(false);
        onError?.("folderId is empty");
        return;
      }

      // 2. 构建 AddPositionAsset 数组，合并 stocks 和 crypto
      const assets: AddPositionAsset[] = [];

      // 2.1 添加 Stocks 分类（资产类型为 STOCK）
      if (selectedByCategory.stocks.length > 0) {
        selectedByCategory.stocks.forEach((assetId) => {
          assets.push({
            asset_id: assetId,
            asset_type: PositionAssetTypeEnum.STOCK,
          });
        });
      }

      // 2.2 添加 Crypto 分类（资产类型为 TOKEN）
      if (selectedByCategory.crypto.length > 0) {
        selectedByCategory.crypto.forEach((assetId) => {
          assets.push({
            asset_id: assetId,
            asset_type: PositionAssetTypeEnum.TOKEN,
          });
        });
      }

      // 3. 如果没有选中的资产，直接返回
      if (assets.length === 0) {
        log.warn("[useAddAssetsWithBatchAdd] 没有选中的资产");
        setIsAdding(false);
        onError?.("no selected assets");
        return;
      }

      // 4. 批量添加资产
      const result = await favoritesCenter.batchAddAssets(assets, folderId);
      if (result.success) {
        onSuccess?.();
        log.info(
          `[useAddAssetsWithBatchAdd] 成功添加 ${result.data?.count || 0} 个资产`,
        );
      } else {
        log.error(
          `[useAddAssetsWithBatchAdd] 批量添加资产失败: ${result.error}`,
        );
        onError?.("batch add assets failed");
      }
    } catch (error) {
      log.error("[useAddAssetsWithBatchAdd] 批量添加资产异常", error);
      onError?.("batch add assets failed");
    } finally {
      setIsAdding(false);
    }
  }, [selectedByCategory, isAdding, onSuccess, onError]);

  return {
    selectedByCategory,
    selectedCount,
    handleSelectionChange,
    isAdding,
    handleAddClick,
  };
};
